
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  SanityObjectSchema,
  ISanityObjects,
  IVColOptions,
  TBreakpoints,
  IColTextFormatting,
} from '~/types/sanity-objects';
import { ThemeName } from '~/store/types';
import ThemeProvider from '~/components/ThemeProvider.vue';
import SanityBlockContent from './SanityBlockContent.vue';

@Component({
  components: {
    SanityBlockContent,
    ThemeProvider,
  },
})
export default class SanityRowWithColumns extends Vue {
  @Prop(Object)
  readonly data!: ISanityObjects[SanityObjectSchema.ROW_WITH_COLUMNS];
  @Prop([String, Object])
  readonly sectionTitleClass!: Record<string, boolean> | string;

  themes = ThemeName;

  get rowProps() {
    const opts = this.data?.vRowOptions;
    if (!opts) {
      return;
    }

    const props: Record<string, string | boolean | undefined> = {
      dense: opts?.gutters === 'dense' || undefined,
      ['no-gutters']: opts?.gutters === 'no-gutters' || undefined,
    };

    (['xs', 'sm', 'md', 'lg', 'xl'] as TBreakpoints[]).forEach((bp) => {
      const suffix = bp === 'xs' ? '' : `-${bp}`;
      if (opts.bpOptions?.[bp]?.align) {
        props[`align${suffix}`] = opts.bpOptions[bp]?.align;
      }
      if (opts.bpOptions?.[bp]?.alignContent) {
        props[`align-content${suffix}`] = opts.bpOptions[bp]?.alignContent;
      }
      if (opts.bpOptions?.[bp]?.justify) {
        props[`justify${suffix}`] = opts.bpOptions[bp]?.justify;
      }
    });

    return props;
  }

  getColTextStyle(opts?: IColTextFormatting) {
    const classes: string[] = [];

    if (opts?.typography?.fontStyle && opts?.typography?.fontSize) {
      classes.push(
        `font--${opts.typography.fontStyle}-${opts.typography.fontSize}`,
      );
    } else {
      classes.push(`font--body-large`);
    }

    if (opts?.bpOptions) {
      Object.keys(opts.bpOptions).forEach((key) => {
        const bp = key as TBreakpoints;
        const textAlign = opts.bpOptions?.[bp]?.textAlign;
        if (textAlign) {
          classes.push(
            bp === 'xs' ? `text-${textAlign}` : `text-${bp}-${textAlign}`,
          );
        }
      });
    }

    return classes.join(' ');
  }

  getColProps(opts?: IVColOptions) {
    if (!opts) {
      return { cols: 12 };
    }

    const props: Record<string, string | number | undefined> = {
      alignSelf: opts?.alignSelf || undefined,
      cols: opts?.bpOptions?.xs?.cols || 12,
      offset: opts?.bpOptions?.xs?.offset || undefined,
      order:
        opts?.bpOptions?.xs?.order !== undefined
          ? opts.bpOptions.xs.order
          : undefined,
    };

    (['sm', 'md', 'lg', 'xl'] as TBreakpoints[]).forEach((bp) => {
      if (opts.bpOptions?.[bp]?.cols !== undefined) {
        props[bp] = opts.bpOptions[bp]?.cols;
      }
      if (opts.bpOptions?.[bp]?.offset !== undefined) {
        props[`offset-${bp}`] = opts.bpOptions[bp]?.offset;
      }
      if (opts.bpOptions?.[bp]?.order !== undefined) {
        props[`order-${bp}`] = opts.bpOptions[bp]?.order;
      }
    });

    return props;
  }
}
